import React from 'react';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import { hashString } from '@wix/da-shared-react/pkg/utils/hash';
import { PapiUser } from '@wix/da-papi-types';
import { WithSectionContextProps } from '../../Sections/SectionContext';
import { ModuleType } from '../../../../types/modules';
import { ZoneType } from '../../../../types/layout';
import AddWidget from '../AddWidget';

export interface Props extends WithSectionContextProps {
  type: ZoneType;
  locked?: boolean;
  isRemovable?: boolean;
  profileOwnerUser: PapiUser;
  isCurrentUserProfileOwner: boolean;
  isAuthorizedToAddModule: boolean;
  isAuthorizedToMoveModule: boolean;
  isMobile: boolean;
  installableModuleTypes: ModuleType[];
  installedModuleIds: number[];
  installedComponents: {
    moduleId: number;
    ComponentClass: React.ComponentType<any>;
  }[];
  mapModuleTypeToWidgetComponent: {
    [moduleName: string]: React.ComponentType<any>;
  };
  isInstallingModule?: boolean;
  currentlyInstalledModulePostion?: number;
  className?: string;
  headerClassName?: string;
  moveModule: (moduleId: number, position: number) => void;
  removeModule: (moduleId: number) => void;
  addModule: (moduleType: ModuleType, position: number) => void;
  children?: React.ReactNode;
}

const defaultProps = {
  installableModuleTypes: [],
  installedModuleIds: [],
  locked: false,
  isRemovable: true,
};

class Zone extends React.PureComponent<Props> {
  static defaultProps = defaultProps;
  render() {
    const {
      section,
      type: zone,
      installedComponents,
      moveModule,
      removeModule,
      isRemovable,
      locked,
      isInstallingModule,
      currentlyInstalledModulePostion,
      isCurrentUserProfileOwner,
      isAuthorizedToMoveModule,
      profileOwnerUser,
      headerClassName,
    } = this.props;

    return (
      <div className={this.props.className} key={this.props.type}>
        {installedComponents.map((installedComponent, idx, modules) => {
          const { moduleId, ComponentClass } = installedComponent;
          const isFirst = idx === 0;
          const isLast = idx === modules.length - 1;
          const canMoveUp = !locked && !isFirst && isAuthorizedToMoveModule;
          const canMoveDown = !locked && !isLast && isAuthorizedToMoveModule;
          const canRemove = !locked && isRemovable;
          const props = {
            key: idx,
            moduleId,
            widgetMoveUp: canMoveUp ? moveModule(moduleId, idx - 1) : undefined,
            widgetMoveDown: canMoveDown
              ? moveModule(moduleId, idx + 1)
              : undefined,
            widgetRemove: canRemove ? removeModule(moduleId) : undefined,
            isCurrentUserProfileOwner,
            profileOwnerUser,
            headerClassName,
            section,
            zone,
          };
          return (
            <ErrorBoundary debugComponent="Zone" key={idx}>
              {isInstallingModule &&
                idx === currentlyInstalledModulePostion &&
                this.renderLoadingIndicator()}
              <ComponentClass {...props} />
              {!isLast && this.renderAddWidgetIfAllowed(idx + 1)}
            </ErrorBoundary>
          );
        })}
      </div>
    );
  }

  renderLoadingIndicator() {
    return <LoadingIndicator view="daSymbolPurple" />;
  }

  renderAddWidgetIfAllowed(position) {
    const {
      type,
      locked,
      isInstallingModule,
      installableModuleTypes,
      isCurrentUserProfileOwner,
      isAuthorizedToAddModule,
      isMobile,
      addModule,
      section,
    } = this.props;

    const isAuthorized = isCurrentUserProfileOwner || isAuthorizedToAddModule;

    if (
      locked ||
      isMobile ||
      !isAuthorized ||
      !installableModuleTypes ||
      !installableModuleTypes.length
    ) {
      return null;
    }
    const installableModules = installableModuleTypes.map(moduleName => ({
      zone: type,
      moduleName,
      position,
    }));

    const uniqueKey = hashString(
      installableModules
        .reduce(
          (result, { zone, moduleName, position: pos }) => [
            ...result,
            `${zone}.${moduleName}.${pos}`,
          ],
          []
        )
        .join(':')
    );

    return (
      <AddWidget
        section={section}
        key={`add-widget-${uniqueKey}`}
        addModule={(zone, moduleName, modulePosition) =>
          addModule(moduleName, modulePosition)
        }
        installableModules={installableModules}
        disabled={isInstallingModule}
      />
    );
  }
}

export default Zone;
